import { createSelector, createSlice } from '@reduxjs/toolkit';
import v4 from 'uuid/v4';
import { clearBankFilters } from 'scenes/ProjectBank/store/actions/project-bank.actions';
import { fetchNerobottiThreads } from './thunks/fetchNerobottiThreads';
import { openNerobottiThread } from './thunks/openNerobottiThread';
import { recoverAbandonedTurn } from './thunks/recoverAbandonedTurn';

/**
 * Nerobotti conversation state.
 *
 * Replaces `scenes/Settings/Chatbot/store/slices/chatbotSlice.js`, which is
 * deprecated along with the rest of the old bot. Three things are deliberately
 * gone:
 *
 *  - `isAdmin` / `isBulletin`. There are no modes any more. The assistant picks
 *    tools based on the question; the user does not pick a data source.
 *  - Everything scope-shaped. The old client sent `businessId`, `projectKey`
 *    and `admin` with every message and the backend believed them, which is how
 *    any authenticated user could read any tenant's documents. The client now
 *    sends the message and nothing that grants anything.
 *  - Silent failure. The old slice had an `evt.type === 'error'` branch that
 *    nothing ever emitted, so a backend failure left `isLoading` true forever
 *    with the send button disabled until reload. Every terminal path here
 *    clears it.
 */

/**
 * Where the conversation is. One field, not several booleans.
 *
 *  - `closed`     — nothing rendered, nothing connected.
 *  - `open`       — on screen: the floating window, or the surface of a scene
 *                   that draws the conversation itself (see `claimNerobotti`).
 *  - `minimized`  — the window is a chip in the corner, still connected.
 *  - `background` — still connected, drawn nowhere. What a conversation on
 *                   screen becomes when the scene drawing it goes away.
 *
 * The distinction that matters is between `closed` and the rest, and it is not
 * cosmetic: `NerobottiConnection` owns the socket, and `NerobottiPanelHost`
 * mounts it for every state but `closed`. Closing therefore drops the
 * connection and abandons whatever turn was in flight, and nothing else does.
 * Minimizing must not — waiting out a slow reply with the window out of the way
 * is the whole point of it — and neither must leaving the document bank in the
 * middle of a conversation, which is what `background` is for. That invariant
 * is the feature; everything else here is bookkeeping around it.
 *
 * An enum rather than `isOpen` + `isMinimized` because those two have a fourth
 * combination — closed *and* minimized — that means nothing, and a reachable
 * meaningless state is exactly what stranded the composer: the store could sit
 * in "awaiting a reply" with no panel and no socket left to deliver one.
 */
export const PANEL_CLOSED = 'closed';
export const PANEL_OPEN = 'open';
export const PANEL_MINIMIZED = 'minimized';
export const PANEL_BACKGROUND = 'background';

const initialState = {
    panelState: PANEL_CLOSED,
    isFullscreen: false,
    /**
     * Something arrived while the panel was minimized and has not been looked
     * at. Drawn as a dot on the chip — a reply that lands behind a docked
     * window is otherwise indistinguishable from one that never came.
     */
    hasUnseenReply: false,
    /**
     * Turns, in order. Two shapes:
     *
     *  - `{ id, role: 'user' | 'assistant', content, status, timestamp }`
     *  - `{ id, role: 'action', actionId, summary, irreversible, expiresAt,
     *     state, outcome, link, timestamp }` — a change the assistant offered,
     *    awaiting a tap. `link` is `{ url, label }` and arrives only once the
     *    change succeeded. `irreversible` comes off the frame, never inferred.
     *
     * The action item is the only interactive thing in the stream, and it is
     * deliberately in the stream rather than in a modal: what is being agreed to
     * belongs next to the sentence that proposed it.
     */
    discussion: [],
    /** Groups turns into one conversation so the assistant can follow context. */
    threadId: null,
    isAwaitingReply: false,
    error: null,
    connectionState: 'idle', // idle | connecting | open | closed

    /**
     * A turn that lost its socket before an answer arrived, as
     * `{ threadId, question }` — or null, which is nearly always.
     *
     * It is here because the server may finish that turn without us. The worker
     * reads its round to the end and files the reply even when nobody is
     * listening, so the answer to this question can already exist on the server
     * while the screen shows only the line saying the connection dropped. This
     * is the note that says to go and look, once there is a socket to look with.
     * See `recoverAbandonedTurn`.
     *
     * Cleared by anything that makes the answer no longer the newest thing on
     * screen — a retry, a new conversation, a thread opened by hand — so a
     * request still in the air cannot land on top of it.
     */
    abandonedTurn: null,
    /**
     * What the user is looking at, published by whichever scene is on screen.
     *
     * `{ owner, view, projectId, bulletinId, folderId }`, or null. Sent with
     * each message as an untrusted hint so the assistant knows what "tämä"
     * refers to; the server allowlists the view and re-checks the ids against
     * the caller's own scope, so nothing here grants anything.
     *
     * `owner` is a per-mount token, not data — see `clearViewContext`. It is
     * stripped before the context is sent.
     */
    viewContext: null,

    /**
     * A question asked and not yet sent. Set by `askNerobotti`, taken by
     * `NerobottiConnection` — see the reducer.
     */
    pendingQuestion: null,

    /**
     * The scene drawing the conversation itself, if one is — a per-mount
     * token, the way `viewContext.owner` is. While it is set the floating
     * window and its chip stay away. See `claimNerobotti`.
     */
    presentationOwner: null,

    /**
     * Which of the two the user last asked for: the conversation, or the list
     * of past ones.
     *
     * Deliberately not "which one is on screen", because that is not a thing
     * one flag can say any more. Where there is only room for one — the 420px
     * dock, and a phone, where a rail beside the stream would leave neither
     * readable — this picks it, and a swap is the whole layout. Where there is
     * room for both, `NerobottiPanel` draws the list as a rail and a
     * conversation beside it whatever this says, and this is only what the
     * panel falls back to when the room goes away.
     *
     * Which is why nothing here reacts to `toggleFullscreen`. Every crossing
     * either way lands on the screen the user last chose, and a window resized
     * across the threshold and back comes back exactly where it was.
     */
    view: 'chat', // chat | history

    /**
     * The conversation list. Paged, so `cursor` is where the next page starts
     * and null means there is no next page.
     */
    threads: {
        items: [],
        status: 'idle', // idle | loading | ready | failed
        cursor: null,
        /** True while a `Load more` is in flight, so the list is not blanked. */
        isLoadingMore: false,
        /** A row failed to open. Shown on the list, not on the chat screen. */
        openFailed: false,
    },

    /** The thread being fetched, so its row alone can show it is opening. */
    openingThreadId: null,

    /**
     * Set when the transcript just loaded was too long to return whole. The
     * panel says so rather than silently showing a conversation that appears to
     * begin in the middle.
     */
    isTranscriptTruncated: false,

    /**
     * The photos a search in this conversation found, as the document bank
     * shows them: a named filter over file ids.
     *
     * Here rather than in the bank's own state because its lifetime is the
     * conversation's. The bank resets its state on unmount, and a filter that
     * vanished on leaving the bank while the reply that made it still said
     * "here are 14 photos" would be two screens disagreeing. It goes with a
     * new conversation, with the conversation being put away, with the
     * bank's own "Tyhjennä", and with its chip's ×.
     *
     * `{ id, projectId, label, fileIds, total, truncated, criteria, folderId?,
     * link? }` — the server's view with an id minted here.
     */
    bankPhotoFilter: null,

    /**
     * Whether the user can ask right now, and on whose account.
     *
     * `{ state, usedPercent, businessId }`, or null until known. `state` is
     * one of `unlimited` (a licensed company, or a Lixani admin), `allowance`
     * (the free tier, with the percentage of the month's allowance used),
     * `exhausted`, `no_business` (several employers and none picked in the
     * top bar) or `no_permission`.
     *
     * Seeded from the all-permissions payload at boot — the API's door answers
     * it beside `mayUseNerobotti` — and kept current by the socket: an
     * `allowance` frame after each turn carries the fresh percentage, and a
     * door refusal's `code` names the state it refused in. Nothing here is
     * derived on the client; the server decides and this only remembers.
     */
    access: null,
};

const ACCESS_STATES = [
    'unlimited',
    'allowance',
    'exhausted',
    'no_business',
    'no_permission',
];

/** The API's `nerobotti` object, or null for anything not shaped like it. */
const accessFromPayload = (nerobotti) => {
    if (!nerobotti || !ACCESS_STATES.includes(nerobotti.state)) return null;

    return {
        state: nerobotti.state,
        usedPercent:
            typeof nerobotti.usedPercent === 'number'
                ? nerobotti.usedPercent
                : null,
        businessId: nerobotti.businessId ?? null,
        // Which company the settings screen manages, and whether this user may
        // open it. Not always the business above: a Lixani system admin is
        // charged to nobody, so the API resolves the screen's company on its
        // own. Both are the API's answer; nothing here derives either, and
        // every endpoint asks again.
        automationsBusinessId: nerobotti.automationsBusinessId ?? null,
        canManageAutomations: nerobotti.canManageAutomations === true,
    };
};

/**
 * The state a door refusal's `code` names, or null for any other error.
 *
 * `nerobotti_allowance_exhausted` folds into the same `exhausted` the
 * percentage reaching a hundred produces, so the panel has one state to draw
 * however it got there.
 */
const DOOR_CODES = {
    nerobotti_no_permission: 'no_permission',
    nerobotti_no_business: 'no_business',
    nerobotti_allowance_exhausted: 'exhausted',
};

/**
 * Turn stored exchanges into the panel's own turn list.
 *
 * One stored row is one *exchange* — both halves together — while the stream
 * renders a sequence of turns, so an ordinary row becomes two items. Ids are
 * minted fresh: they only ever key the React list, and nothing stored is stable
 * enough to reuse for that.
 *
 * ## A confirmed action is one card, not two messages
 *
 * The server files a confirmation as a turn whose `userMessage` is the summary
 * behind a `[Vahvistettu]` prefix and whose `assistantMessage` is the outcome —
 * a shape chosen for the *model*, which is shown the confirmation as if the
 * user had spoken it. Replaying that literally is what made a reopened thread
 * look wrong: a bubble for a sentence nobody typed, a second bubble for text
 * that belongs on the card, and no card at all.
 *
 * Live, the outcome never becomes a message — `receiveSocketEvent` routes a
 * `done` carrying an `actionId` onto the card instead. So one row with an
 * `action` becomes exactly one `action` item, and the two messages are dropped.
 * That is not hiding them: the card carries both, in the places they belong.
 *
 * ## Rows written before the server recorded the outcome
 *
 * Those get the same card in an `archived` state: the summary and the outcome
 * text are both in the row, only the verdict is missing, so the layout matches
 * live and the rule stays grey rather than claiming a success or a failure.
 *
 * Recognising them does mean matching the Finnish `[Vahvistettu]` prefix — the
 * exact parsing the stored `action` record exists to avoid. It is acceptable
 * *here* and nowhere else: it applies only to rows that can never gain that
 * record, and it asserts nothing the prefix does not already say. It is
 * deliberately not a fallback for new rows, which is why it does not try to
 * infer a status. These age out with the 90-day retention, and this goes with
 * them.
 */
/**
 * The marker the server puts in front of a confirmation's summary when it files
 * it as a turn, because the *model* is shown the confirmation as though the user
 * had spoken it.
 */
const CONFIRMED_PREFIX = '[Vahvistettu] ';

/**
 * The summary of a confirmation stored before the server recorded its outcome,
 * or null for any other turn.
 *
 * Requires the outcome half to be present too. That does not make the match
 * airtight — someone could type the prefix themselves and get their own
 * sentence drawn as a grey card — but the cost of that is cosmetic and confined
 * to their own transcript, which is the right side to be wrong on for a
 * fallback that exists only until these rows expire.
 */
const legacyConfirmSummary = (turn) => {
    const userMessage = turn?.userMessage;

    if (typeof userMessage !== 'string') return null;
    if (!userMessage.startsWith(CONFIRMED_PREFIX)) return null;
    if (!turn?.assistantMessage) return null;

    const summary = userMessage.slice(CONFIRMED_PREFIX.length).trim();

    return summary || null;
};

const discussionFromTurns = (turns) =>
    turns.flatMap((turn) => {
        const parsed = turn?.createdAt ? Date.parse(turn.createdAt) : NaN;
        const timestamp = Number.isNaN(parsed) ? Date.now() : parsed;

        /** Every replayed card is spent, so none of them can be acted on. */
        const settledCard = (summary, state, link) => ({
            id: v4(),
            role: 'action',
            // No actionId: the offer is long spent, and a terminal card renders
            // no buttons, so nothing can be dispatched for it.
            actionId: null,
            summary,
            // Nothing to warn about on a card nobody can act on, and the stored
            // row does not say either way. What it was is history now, and the
            // state colour is the part of it worth drawing.
            irreversible: false,
            state,
            outcome: turn.assistantMessage || null,
            link,
            expiresAt: null,
            timestamp,
        });

        if (turn?.action?.summary) {
            return [
                settledCard(
                    turn.action.summary,
                    turn.action.status === 'ok' ? 'done' : 'failed',
                    turn.action.link ?? null
                ),
            ];
        }

        const legacySummary = legacyConfirmSummary(turn);

        if (legacySummary) {
            return [settledCard(legacySummary, 'archived', null)];
        }

        const items = [];

        if (turn?.userMessage) {
            items.push({
                id: v4(),
                role: 'user',
                content: turn.userMessage,
                status: 'final',
                timestamp,
            });
        }

        if (turn?.assistantMessage) {
            items.push({
                id: v4(),
                role: 'assistant',
                content: turn.assistantMessage,
                status: 'final',
                timestamp,
            });
        }

        return items;
    });

export const nerobottiSlice = createSlice({
    name: 'nerobotti',
    initialState,
    reducers: {
        openNerobotti: (state) => {
            showPanel(state);
        },

        /**
         * Closing takes the panel down, and with it the socket — so this is the
         * moment the in-flight turn dies, and the moment to say so.
         *
         * Nothing else was saying it. The teardown deliberately nulls
         * `onclose` before closing the socket so a panel being dismissed cannot
         * schedule a reconnect, which means `setConnectionState('closed')` —
         * the one path that clears `isAwaitingReply` — never runs. The store
         * was left waiting for a reply that no longer had anything to arrive
         * on, and reopening could not clear it either: the panel comes back at
         * `idle`, and this reducer returns early for every state but `closed`.
         * The composer stayed disabled until the user started a new
         * conversation or opened an old one.
         *
         * That is the bug the assistant this replaces was built to be rid of —
         * a UI stuck waiting forever — arriving through the one door the
         * teardown comments do not cover. It is fixed here rather than in the
         * socket because closing is the fact we actually have: the turn is not
         * unreachable because a connection dropped, it is unreachable because
         * the user dismissed the window it was going to land in.
         */
        closeNerobotti: (state) => {
            closePanel(state);
        },

        /**
         * Out of the way, still listening.
         *
         * Deliberately touches nothing but the lifecycle: the connection stays
         * mounted, so the socket, the thread and any turn mid-flight all
         * continue. That is the difference from `closeNerobotti`, and it is the
         * reason a user waiting on a slow answer now has somewhere to put the
         * window other than away.
         */
        minimizeNerobotti: (state) => {
            if (state.panelState !== PANEL_OPEN) return;

            state.panelState = PANEL_MINIMIZED;
        },

        restoreNerobotti: (state) => {
            showPanel(state);
        },

        /**
         * The launcher button.
         *
         * A minimized panel restores rather than closes — the chip is the user
         * saying "later", and the same button that docked it should be able to
         * bring it back without costing them the conversation.
         */
        toggleNerobotti: (state) => {
            if (state.panelState === PANEL_OPEN) {
                closePanel(state);
                return;
            }

            showPanel(state);
        },

        toggleFullscreen: (state) => {
            state.isFullscreen = !state.isFullscreen;
        },

        /**
         * A question, from wherever it was asked: the floating panel's composer
         * or one of its starters, or the document bank's AI panel.
         *
         * Queued rather than sent, because only `NerobottiConnection` can send
         * and there may not be one yet. Opening mounts it, it takes the
         * question and sends it, and the socket's own queue holds it until the
         * connection that mount opened is up.
         *
         * From closed it starts a new conversation. Nothing is showing a
         * closed conversation when a question arrives from there — the bank's
         * panel shows its search field instead — so it is a new question, not
         * a reply to whatever the store last held, and that conversation is one
         * tap away in the history.
         */
        askNerobotti: (state, action) => {
            const text =
                typeof action.payload?.text === 'string'
                    ? action.payload.text.trim()
                    : '';

            if (!text) return;

            if (state.panelState === PANEL_CLOSED) {
                resetConversation(state);
            }

            showPanel(state);
            state.pendingQuestion = text;
        },

        /** The connection has the question and is sending it. */
        takePendingQuestion: (state) => {
            state.pendingQuestion = null;
        },

        /**
         * A scene saying it draws the conversation itself — the document
         * bank's AI panel — so the floating window and its chip must not.
         *
         * Nothing else changes. The conversation, its connection and whatever
         * reply is on the way carry on exactly as they were; only where they
         * are drawn moves. Last claimant wins, the same as `setViewContext`,
         * because the scene mounting now is the one the user is looking at.
         */
        claimNerobotti: (state, action) => {
            state.presentationOwner = action.payload ?? null;
        },

        /**
         * The claiming scene unmounting.
         *
         * A conversation that was on screen goes to the background, not into
         * the floating window. The user left a screen, not the conversation,
         * and a window thrown up over the next screen is not something they
         * asked for. It stays connected, so a reply still being written lands
         * in the store, and the next place to show the conversation — the
         * launcher on another screen, or the bank on the way back — shows it
         * finished.
         *
         * Only the claimant's own release counts, for the reason
         * `clearViewContext` checks its owner.
         */
        releaseNerobotti: (state, action) => {
            if (state.presentationOwner !== action.payload) return;

            state.presentationOwner = null;

            if (
                state.panelState === PANEL_OPEN ||
                state.panelState === PANEL_MINIMIZED
            ) {
                state.panelState = PANEL_BACKGROUND;
            }
        },

        /**
         * Clear the conversation. A new threadId means a fresh context.
         *
         * This used to be the only way to lose a conversation and it lost it
         * for good — the turns were on the server the whole time, with no way
         * to ask for them. It is an ordinary action now: what it clears is the
         * screen and the pointer, and the conversation is one tap away in the
         * history view.
         */
        startNewConversation: (state) => {
            resetConversation(state);
        },

        showHistory: (state) => {
            state.view = 'history';
        },

        showChat: (state) => {
            state.view = 'chat';
        },

        setConnectionState: (state, action) => {
            state.connectionState = action.payload;

            if (action.payload !== 'closed') return;

            // A socket that closed while we were waiting is a dead turn. Say so
            // rather than leaving a placeholder spinning.
            abandonInFlightTurn(
                state,
                'Yhteys katkesi. Yritä uudelleen.',
                'Yhteys katkesi. Tarkista tiedotetaulu ennen kuin yrität uudelleen.'
            );
        },

        /** Optimistic: the user's turn plus the placeholder deltas fill in. */
        sendMessage: (state, action) => {
            const { text, threadId } = action.payload;

            // The caller mints the id and passes it in, rather than this
            // reducer inventing one. Minting it here looks tidier and is a
            // trap: `dispatch` does not re-render synchronously, so the panel
            // that just dispatched still reads `null` and sends the message
            // unthreaded. The server then drops the turn on the floor —
            // `saveTurn` requires a thread id — and the first exchange of every
            // conversation is invisible to the next one.
            if (!state.threadId) {
                state.threadId = threadId ?? `${new Date().toISOString()}#${v4()}`;
            }

            state.error = null;
            state.isAwaitingReply = true;
            // The user got tired of waiting and asked again. Whatever the last
            // turn left behind must not now arrive and redraw the screen
            // underneath the one in flight.
            state.abandonedTurn = null;

            state.discussion.push({
                id: v4(),
                role: 'user',
                content: text,
                status: 'final',
                timestamp: Date.now(),
            });

            state.discussion.push({
                id: v4(),
                role: 'assistant',
                content: '',
                status: 'streaming',
                timestamp: Date.now(),
            });
        },

        /** A frame from the socket: delta | done | error | confirm | pong. */
        receiveSocketEvent: (state, action) => {
            const event = action.payload;
            const last = lastAssistantTurn(state);

            // Terminal frames only. A `delta` means the reply is still being
            // written, and a dot that lights up the instant the assistant
            // starts typing says nothing about whether it is worth going back
            // for; the frame that ends the turn does.
            if (
                state.panelState === PANEL_MINIMIZED &&
                ['done', 'confirm', 'error'].includes(event?.type)
            ) {
                state.hasUnseenReply = true;
            }

            switch (event?.type) {
                case 'delta': {
                    // The old slice did `last.content += ...` unguarded, which
                    // throws if a delta arrives with no placeholder (a late
                    // frame from an abandoned turn, for instance).
                    if (last && typeof event.text === 'string') {
                        last.content += event.text;
                    }
                    break;
                }

                case 'done': {
                    // A `done` carrying an actionId is the outcome of a
                    // confirmed action, not the end of a reply. It must not
                    // touch the last assistant turn: no placeholder was created
                    // for a button press, so `last` is a finished message from
                    // earlier and writing to it would overwrite real text.
                    if (event.actionId) {
                        resolveActionTurn(state, event);
                        state.isAwaitingReply = false;
                        break;
                    }

                    if (last) {
                        // Prefer the authoritative full text over accumulated
                        // deltas — a dropped frame would otherwise leave a gap.
                        if (typeof event.text === 'string' && event.text) {
                            last.content = event.text;
                        }
                        last.status = 'final';
                    }
                    state.isAwaitingReply = false;
                    break;
                }

                /**
                 * The assistant is offering to change something. Arrives after
                 * `done`, so the reply bubble is already final and the composer
                 * already unlocked — the card lands underneath a finished
                 * message.
                 */
                case 'confirm': {
                    if (!event.actionId) break;

                    state.discussion.push({
                        id: v4(),
                        role: 'action',
                        actionId: event.actionId,
                        // The server's sentence, not the model's prose. This is
                        // the thing being agreed to.
                        summary: event.summary || '',
                        /**
                         * Whether accepting this can be taken back.
                         *
                         * Read off the frame and never inferred here. The
                         * client could recognise a list of tool names instead,
                         * and that is exactly the wrong place for it: the
                         * registry would gain a write this list had never heard
                         * of and the card would go on looking harmless, with
                         * nothing failing to say so. The server knows which of
                         * its own tools cannot be undone; a client guess would
                         * only ever be a stale copy of that.
                         *
                         * Strict `=== true` for the same reason: a frame that
                         * does not carry the field is one that says nothing
                         * about reversibility, not one that says there is
                         * nothing to warn about. That is what an older server
                         * sends, and drawing its offers as harmless would be a
                         * claim nobody made.
                         */
                        irreversible: event.irreversible === true,
                        expiresAt: event.expiresAt ?? null,
                        state: 'pending',
                        outcome: null,
                        link: null,
                        timestamp: Date.now(),
                    });
                    break;
                }

                case 'error': {
                    state.isAwaitingReply = false;
                    state.error = event.message || 'Jokin meni vikaan.';

                    // A refusal at the door is also a state, not only a
                    // message: the composer stays closed until it changes,
                    // where an ordinary error is over once dismissed.
                    const refusedIn = DOOR_CODES[event.code];
                    if (refusedIn) {
                        state.access = {
                            ...(state.access ?? {
                                businessId: null,
                                automationsBusinessId: null,
                                canManageAutomations: false,
                            }),
                            state: refusedIn,
                            usedPercent:
                                typeof event.usedPercent === 'number'
                                    ? event.usedPercent
                                    : refusedIn === 'exhausted'
                                    ? 100
                                    : state.access?.usedPercent ?? null,
                        };
                    }

                    // Same split as `done`: an error about an action belongs on
                    // its card, not appended to an unrelated message.
                    if (event.actionId) {
                        resolveActionTurn(state, {
                            ...event,
                            text: state.error,
                            status: 'error',
                        });
                        break;
                    }

                    failLastAssistantTurn(state, state.error);
                    break;
                }

                /**
                 * Where the month's allowance stands after the turn just
                 * answered. Sent last, after `done`, so the figure moves once
                 * the reply is there. Only ever sent to a free-tier caller, so
                 * it never has to argue with `unlimited`.
                 */
                case 'allowance': {
                    if (typeof event.usedPercent !== 'number') break;

                    state.access = {
                        ...(state.access ?? {
                            businessId: null,
                            automationsBusinessId: null,
                            canManageAutomations: false,
                        }),
                        state: event.usedPercent >= 100 ? 'exhausted' : 'allowance',
                        usedPercent: event.usedPercent,
                    };
                    break;
                }

                default:
                    break;
            }
        },

        /**
         * The user tapped Vahvista. Optimistic only in appearance — nothing is
         * written until the server says so, and the server re-checks permissions
         * before it does.
         */
        confirmAction: (state, action) => {
            const turn = findActionTurn(state, action.payload?.actionId);
            if (!turn || turn.state !== 'pending') return;

            state.error = null;
            turn.state = 'confirming';
            state.isAwaitingReply = true;
        },

        /** The user dismissed the card. Client-side only; the offer just expires. */
        dismissAction: (state, action) => {
            const turn = findActionTurn(state, action.payload?.actionId);
            if (!turn || turn.state !== 'pending') return;

            turn.state = 'dismissed';
        },

        /** The confirm frame never left the client. */
        confirmFailed: (state, action) => {
            const { actionId, message } = action.payload ?? {};
            const turn = findActionTurn(state, actionId);

            state.isAwaitingReply = false;

            if (!turn) return;

            turn.state = 'failed';
            turn.outcome = message || 'Vahvistuksen lähetys epäonnistui.';
        },

        /** The socket refused the message outright (never reached the server). */
        sendFailed: (state, action) => {
            state.isAwaitingReply = false;
            state.error = action.payload || 'Viestin lähetys epäonnistui.';
            failLastAssistantTurn(state, state.error);
        },

        dismissError: (state) => {
            state.error = null;
        },

        /**
         * A photo search answered, and the bank is to show its matches.
         * Replaces the previous one: two filters at once would be two answers
         * to "what am I looking at", and the newer question wins.
         */
        setBankPhotoFilter: (state, action) => {
            state.bankPhotoFilter = action.payload ?? null;
        },

        /** The chip's ×. The conversation goes on; only the filter is gone. */
        clearBankPhotoFilter: (state) => {
            state.bankPhotoFilter = null;
        },

        /**
         * Links under the newest reply — a search made from another screen
         * carries one that opens the bank on its matches. On the turn rather
         * than the store, so a reply keeps its link when the next one comes.
         */
        attachReplyLinks: (state, action) => {
            const last = lastAssistantTurn(state);
            const links = Array.isArray(action.payload)
                ? action.payload.filter(
                      (link) =>
                          typeof link?.url === 'string' &&
                          typeof link?.label === 'string'
                  )
                : [];

            if (!last || !links.length) return;

            last.links = links;
        },

        /**
         * A scene saying what it is showing.
         *
         * Last writer wins, deliberately: the panel is global and outlives any
         * one scene, so the context should always describe the screen the user
         * is on now rather than the one they opened the assistant from.
         */
        setViewContext: (state, action) => {
            const { owner, ...context } = action.payload ?? {};
            state.viewContext = { owner, ...context };
        },

        /**
         * A scene unmounting.
         *
         * Only clears its *own* context. Without the owner check a scene tearing
         * down after its replacement has already mounted would wipe the new
         * scene's context, leaving the assistant blind on a screen that had
         * just described itself — a bug that would show up as "the hint works
         * except when you navigate between two views that both publish one".
         */
        clearViewContext: (state, action) => {
            if (state.viewContext?.owner !== action.payload) return;

            state.viewContext = null;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchNerobottiThreads.pending, (state, action) => {
                // A `Load more` must not blank the list under the user's
                // finger, so only a first page takes the loading state.
                if (action.meta.arg?.cursor) {
                    state.threads.isLoadingMore = true;
                    return;
                }

                state.threads.status = 'loading';
            })
            .addCase(fetchNerobottiThreads.fulfilled, (state, action) => {
                const { threads, cursor, append } = action.payload;

                state.threads.items = append
                    ? [...state.threads.items, ...threads]
                    : threads;
                state.threads.cursor = cursor;
                state.threads.status = 'ready';
                state.threads.isLoadingMore = false;
            })
            .addCase(fetchNerobottiThreads.rejected, (state) => {
                state.threads.isLoadingMore = false;
                // Keeping whatever is already listed: a failed *next* page
                // should cost the new rows, not the ones already being read.
                state.threads.status = state.threads.items.length
                    ? 'ready'
                    : 'failed';
            })

            .addCase(openNerobottiThread.pending, (state, action) => {
                state.openingThreadId = action.meta.arg;
                state.threads.openFailed = false;
            })
            .addCase(openNerobottiThread.fulfilled, (state, action) => {
                const { threadId, turns, truncated } = action.payload;

                state.discussion = discussionFromTurns(turns);
                // Adopting the id is what lets the conversation continue rather
                // than merely be read — see the thunk.
                state.threadId = threadId;
                state.isTranscriptTruncated = truncated;
                state.openingThreadId = null;
                // Not "close the list" — beside a rail the list stays up and
                // this is unseen. It is the same sentence `view` always said:
                // the conversation is what was asked for last. That is what
                // makes leaving fullscreen land on the conversation the user
                // picked in the rail rather than back on the list they picked
                // it from.
                state.view = 'chat';
                // A loaded conversation has nothing in flight by construction.
                state.isAwaitingReply = false;
                // And this transcript is newer than anything a recovery could
                // add, including when it is the same thread: the user asked for
                // it by hand.
                state.abandonedTurn = null;
                // The filter belonged to the conversation just swapped out.
                state.bankPhotoFilter = null;
            })
            .addCase(openNerobottiThread.rejected, (state) => {
                state.openingThreadId = null;
                // Kept out of `state.error`, which holds text the panel prints
                // as-is. This failure belongs to the list the user is still
                // looking at, and the list translates it there.
                state.threads.openFailed = true;
            })

            /**
             * The answer that finished while nobody was connected, if there was
             * one.
             *
             * Deliberately silent when there was not. This request goes out on
             * every reconnect that follows a dropped turn, and most of them find
             * nothing: the worker now checks the socket before the first model
             * call, so a client that left early costs no turn and files none.
             * Those have to leave the screen exactly as it was — the question,
             * and the line under it saying the connection dropped, which is the
             * truth in that case.
             */
            .addCase(recoverAbandonedTurn.fulfilled, (state, action) => {
                const { threadId, question, turns, truncated } = action.payload;

                // The store may have moved on while the request was in the air.
                // Every writer that clears the note has a better claim to the
                // screen than a transcript fetched before they ran.
                if (
                    !state.abandonedTurn ||
                    state.abandonedTurn.threadId !== threadId
                ) {
                    return;
                }

                state.abandonedTurn = null;

                const last = turns[turns.length - 1];

                // The turn is only recovered if the thread's newest exchange is
                // *this* question, answered. Matching on the question rather
                // than on the count is what keeps a thread that already ended
                // with some older answer from being read as this one having
                // finished.
                if (
                    !last ||
                    last.userMessage !== question ||
                    !last.assistantMessage
                ) {
                    return;
                }

                // Rebuilt from the transcript rather than patched into the
                // failed bubble: the server's copy is the record, and it carries
                // the settled cards for anything the turn confirmed along the
                // way.
                state.discussion = discussionFromTurns(turns);
                state.isTranscriptTruncated = truncated;

                // Same rule as a `done` frame arriving at a minimised panel —
                // the dot means there is something worth coming back for, and
                // now there is.
                if (state.panelState === PANEL_MINIMIZED) {
                    state.hasUnseenReply = true;
                }
            })
            .addCase(recoverAbandonedTurn.rejected, (state, action) => {
                if (
                    !state.abandonedTurn ||
                    state.abandonedTurn.threadId !== action.meta.arg?.threadId
                ) {
                    return;
                }

                // One attempt, then let it go. The line already on screen still
                // says the right thing, and the conversation is in the history
                // list either way — where opening it does the same fetch,
                // deliberately.
                state.abandonedTurn = null;
            })

            // The bank's "Tyhjennä" clears every filter on its screen, and
            // the photo filter is one of them whatever slice it lives in.
            .addCase(clearBankFilters, (state) => {
                state.bankPhotoFilter = null;
            })

            /**
             * The door's answer rides the all-permissions payload, so the
             * panel opens in the right state without a request of its own.
             * Matched by action type rather than by importing the thunk: that
             * thunk pulls in the permissions store, and this slice has no
             * other reason to. A reload — after an employer switch, say —
             * replaces whatever the socket had said since, which is right:
             * the payload is the newer word. (Last in the chain because the
             * builder wants every `addCase` before the first `addMatcher`.)
             */
            .addMatcher(
                (action) =>
                    action.type ===
                    'permissions/loadAllCurrentUserPermissions/fulfilled',
                (state, action) => {
                    state.access = accessFromPayload(action.payload?.nerobotti);
                }
            );
    },
});

/** Bring the conversation up: from closed, the chip or the background. */
const showPanel = (state) => {
    state.panelState = PANEL_OPEN;
    state.hasUnseenReply = false;
};

/** A blank conversation: no turns, no thread, nothing waited on. */
const resetConversation = (state) => {
    state.discussion = [];
    state.threadId = null;
    state.isAwaitingReply = false;
    state.error = null;
    state.isTranscriptTruncated = false;
    state.bankPhotoFilter = null;
    // Whatever was waiting to be recovered belongs to the conversation just
    // left. It is still in the history list; it is no longer this screen.
    state.abandonedTurn = null;
    // Starting a conversation means being in one.
    state.view = 'chat';
};

/**
 * Take the window down, and everything that was only going to arrive in it.
 *
 * See `closeNerobotti` for why the abandonment belongs here and not in the
 * socket's teardown.
 */
const closePanel = (state) => {
    state.panelState = PANEL_CLOSED;
    state.hasUnseenReply = false;
    // No socket any more, and none wanted. Leaving 'open' here would have the
    // reopened panel believe it was already connected.
    state.connectionState = 'idle';
    // A question that no panel got to send goes with it.
    state.pendingQuestion = null;
    // And so does the filter its answers put on the bank: a conversation put
    // away is not the one the user is looking at.
    state.bankPhotoFilter = null;

    abandonInFlightTurn(
        state,
        'Keskustelu suljettiin ennen vastausta.',
        'Ikkuna suljettiin kesken vahvistuksen. Tarkista lopputulos ennen kuin yrität uudelleen.'
    );
};

/**
 * Everything that was waiting on a socket we no longer have.
 *
 * Two callers, two reasons the socket is gone — it dropped, or the user closed
 * the panel it belonged to — and the same two victims either way: the streaming
 * placeholder, and any card the user had already authorised.
 *
 * A card still in 'pending' is deliberately left alone. The offer lives
 * server-side keyed by user, not by connection, so it survives a reconnect and
 * stays tappable — and it survives the panel being closed and reopened too.
 */
const abandonInFlightTurn = (state, replyMessage, confirmMessage) => {
    if (state.isAwaitingReply) {
        state.isAwaitingReply = false;
        failLastAssistantTurn(state, replyMessage);

        // The turn is unreachable from here, which is not the same as over: the
        // worker finishes what it started and files the reply. Remember which
        // question to go back for, so the next socket can ask. Only with a
        // thread id — an unthreaded turn is one the server could not have filed
        // either.
        if (state.threadId) {
            const question = lastUserQuestion(state);

            if (question) {
                state.abandonedTurn = { threadId: state.threadId, question };
            }
        }
    }

    // A card mid-confirm is the same problem with worse consequences: the write
    // may or may not have happened, and there is no longer a socket to be told
    // which. Say exactly that rather than implying either.
    state.discussion.forEach((turn) => {
        if (turn.role === 'action' && turn.state === 'confirming') {
            turn.state = 'failed';
            turn.outcome = confirmMessage;
        }
    });
};

/**
 * The streaming placeholder, if there is one.
 *
 * Note this looks only at the *last* item, so a card sitting at the end of the
 * discussion correctly yields null — a stray delta after an offer has nowhere to
 * go rather than landing on the wrong turn.
 */
const lastAssistantTurn = (state) => {
    const last = state.discussion[state.discussion.length - 1];
    return last && last.role === 'assistant' ? last : null;
};

const findActionTurn = (state, actionId) =>
    actionId
        ? state.discussion.find(
              (turn) => turn.role === 'action' && turn.actionId === actionId
          )
        : undefined;

/**
 * Record what became of a confirmed action.
 *
 * Keyed by actionId rather than by position: the outcome arrives on its own
 * invocation and the user may have sent other messages in between, so the card
 * is not necessarily the last thing in the stream.
 */
const resolveActionTurn = (state, event) => {
    const turn = findActionTurn(state, event.actionId);
    if (!turn) return;

    // Never downgrade a card that already succeeded. A second tap is refused by
    // the server with "already done", which is a true statement but arrives
    // without an ok status — treating it as a failure would turn a bulletin that
    // was genuinely posted red.
    if (turn.state === 'done') return;

    turn.state = event.status === 'ok' ? 'done' : 'failed';
    turn.outcome = event.text || null;
    // Built by the server from ids it already holds — never parsed out of the
    // assistant's prose, for the same reason `summary` isn't.
    turn.link = event.link ?? null;
};

/**
 * The question the user is still waiting on.
 *
 * Read backwards rather than taken as the second-to-last item: an abandoned
 * turn is a user turn followed by a placeholder, but a card can sit between
 * them, and the confirm path pushes one.
 */
const lastUserQuestion = (state) => {
    for (let i = state.discussion.length - 1; i >= 0; i -= 1) {
        if (state.discussion[i].role === 'user') {
            return state.discussion[i].content;
        }
    }

    return null;
};

/**
 * Turn a stuck placeholder into a visible failure. Without this the user sees
 * an empty bubble and a spinner that never resolves — the single worst
 * behaviour of the bot this replaces.
 */
const failLastAssistantTurn = (state, message) => {
    const last = lastAssistantTurn(state);
    if (!last || last.status === 'final') return;

    last.status = 'error';
    last.content = last.content
        ? `${last.content}\n\n_${message}_`
        : `_${message}_`;
};

export const {
    openNerobotti,
    closeNerobotti,
    minimizeNerobotti,
    restoreNerobotti,
    toggleNerobotti,
    toggleFullscreen,
    askNerobotti,
    takePendingQuestion,
    claimNerobotti,
    releaseNerobotti,
    startNewConversation,
    setConnectionState,
    sendMessage,
    receiveSocketEvent,
    sendFailed,
    dismissError,
    setBankPhotoFilter,
    clearBankPhotoFilter,
    attachReplyLinks,
    confirmAction,
    dismissAction,
    confirmFailed,
    setViewContext,
    clearViewContext,
    showHistory,
    showChat,
} = nerobottiSlice.actions;

// SELECTORS
export const selectNerobotti = (state) => state.nerobottiSlice ?? initialState;
export const selectNerobottiPanelState = (state) =>
    selectNerobotti(state).panelState;

/**
 * On screen. Not the same as mounted — a minimized panel is still mounted and
 * still connected, which is why the host has its own check and this one is only
 * good for the launcher's `aria-expanded`.
 */
export const selectIsNerobottiOpen = (state) =>
    selectNerobottiPanelState(state) === PANEL_OPEN;

export const selectHasUnseenNerobottiReply = (state) =>
    selectNerobotti(state).hasUnseenReply;
export const selectDiscussion = (state) => selectNerobotti(state).discussion;
export const selectIsAwaitingReply = (state) =>
    selectNerobotti(state).isAwaitingReply;
export const selectThreadId = (state) => selectNerobotti(state).threadId;
export const selectNerobottiError = (state) => selectNerobotti(state).error;
/** The dropped turn worth asking the server about, or null. */
export const selectAbandonedTurn = (state) =>
    selectNerobotti(state).abandonedTurn;
export const selectPendingQuestion = (state) =>
    selectNerobotti(state).pendingQuestion;

/** Whether a scene is drawing the conversation, so that no window may. */
export const selectIsNerobottiClaimed = (state) =>
    Boolean(selectNerobotti(state).presentationOwner);

/** The photo filter the conversation put on the document bank, or null. */
export const selectBankPhotoFilter = (state) =>
    selectNerobotti(state).bankPhotoFilter;

export const selectNerobottiView = (state) => selectNerobotti(state).view;
export const selectNerobottiThreads = (state) => selectNerobotti(state).threads;
export const selectOpeningThreadId = (state) =>
    selectNerobotti(state).openingThreadId;
export const selectIsTranscriptTruncated = (state) =>
    selectNerobotti(state).isTranscriptTruncated;
export const selectNerobottiAccess = (state) => selectNerobotti(state).access;
export const selectCanManageAutomations = (state) =>
    selectNerobottiAccess(state)?.canManageAutomations === true;

/**
 * Whether the socket is up. The panel says so when it isn't: a dead socket is
 * the failure a user is most likely to hit here, and until now the state was
 * recorded and shown nowhere.
 */
export const selectConnectionState = (state) =>
    selectNerobotti(state).connectionState;

/**
 * The context as it goes on the wire: the scene's description of itself,
 * without the bookkeeping token.
 *
 * Returns undefined rather than null when there is nothing, so it drops out of
 * the JSON frame instead of being sent as an explicit null.
 *
 * The one memoized selector in this file, and it has to be: it builds a new
 * object, so an unmemoized version would hand `useSelector` a fresh reference
 * on every dispatch and re-render the panel on every action in the app —
 * including each of its own streaming deltas.
 */
export const selectViewContextForSend = createSelector(
    (state) => selectNerobotti(state).viewContext,
    (context) => {
        if (!context) return undefined;

        const { owner, ...rest } = context;

        return rest;
    }
);
